import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Plus, X, Pencil } from "lucide-react";
import api from "../lib/api";
import { useToast } from "../hooks/use-toast";

const EditRequestModal = ({ open, onClose, request, onUpdated }) => {
  const { toast } = useToast();
  const [form, setForm] = useState({ title: "", description: "", area: "", deadline: "" });
  const [tasks, setTasks] = useState([]);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    if (request) {
      setForm({ title: request.title || "", description: request.description || "", area: request.area || "", deadline: request.deadline || "" });
      setTasks((request.tasks || []).map((t) => ({ id: t.id, title: t.title })));
    }
  }, [request]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, tasks: tasks.filter((t) => (t.title || "").trim()).map((t) => ({ id: t.id, title: t.title })) };
      const { data } = await api.put(`/requests/${request.id}`, payload);
      toast({ title: "Request updated", description: data.request.title });
      onUpdated && onUpdated();
      onClose();
    } catch (err) {
      toast({ title: "Could not update request", description: err?.response?.data?.detail || "Try again.", variant: "destructive" });
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="edit-request-modal" className="sm:max-w-lg rounded-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="mb-2 flex h-12 w-12 items-center justify-center rounded-2xl bg-indigo-50"><Pencil className="h-6 w-6 text-indigo-700" /></div>
          <DialogTitle className="text-2xl font-bold text-[#1a1a2e]">Edit request</DialogTitle>
          <DialogDescription className="text-slate-500">Update the request details, deadline and tasks.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="mt-2 space-y-3">
          <div className="space-y-1.5"><Label>Title</Label><Input data-testid="edit-request-title" required value={form.title} onChange={set("title")} className="rounded-xl" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Deadline</Label><Input data-testid="edit-request-deadline" type="date" value={form.deadline} onChange={set("deadline")} className="rounded-xl" /></div>
            <div className="space-y-1.5"><Label>Area</Label><Input data-testid="edit-request-area" value={form.area} onChange={set("area")} placeholder="e.g. Tax, Audit, GST" className="rounded-xl" /></div>
          </div>
          <div className="space-y-1.5"><Label>Description</Label><Textarea data-testid="edit-request-description" value={form.description} onChange={set("description")} rows={2} className="rounded-xl" /></div>
          <div className="space-y-2">
            <Label>Tasks</Label>
            {tasks.map((t, i) => (
              <div key={t.id || i} className="flex items-center gap-2">
                <Input value={t.title} onChange={(e) => setTasks(tasks.map((x, j) => (j === i ? { ...x, title: e.target.value } : x)))} placeholder={`Task ${i + 1}`} className="rounded-xl" />
                <button type="button" onClick={() => setTasks(tasks.filter((_, j) => j !== i))} className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-rose-500"><X className="h-4 w-4" /></button>
              </div>
            ))}
            <button type="button" onClick={() => setTasks([...tasks, { title: "" }])} className="inline-flex items-center gap-1 text-sm font-medium text-[#2f2b8f] hover:underline"><Plus className="h-4 w-4" /> Add task</button>
          </div>
          <Button data-testid="edit-request-save" type="submit" disabled={busy} className="w-full rounded-xl bg-[#2f2b8f] py-6 text-sm font-semibold text-white hover:bg-[#26236f]">
            {busy ? "Saving..." : "Save changes"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditRequestModal;
